import React, { useEffect, useState } from 'react';
import { premiumService } from '../services/api';
import Sidebar from '../components/Sidebar';
import './PremiumEntries.css';

const TYPE_LABELS = {
  NIGHTSHIFT:        'Nachtschicht',
  SUNDAY:            'Sonntagsarbeit',
  HOLIDAY:           'Feiertagsarbeit',
  CHRISTMAS_NEWYEAR: 'Heiligabend / Silvester',
  SATURDAY_OVERTIME: 'Samstag-Zuschlag',
};

function getInitials(alias, username) {
  const name = alias || username || '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatBonus(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function PremiumEntries() {
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [booking,  setBooking]  = useState(null); // timesheetId being booked
  const [deleteTarget,  setDeleteTarget]  = useState(null); // Eintrag im Loesch-Dialog
  const [deleteComment, setDeleteComment] = useState('');
  const [deleting,      setDeleting]      = useState(false);
  const [deleteError,   setDeleteError]   = useState('');
  const [notice,        setNotice]        = useState(null); // { text, warn }

  useEffect(() => {
    premiumService.getEntries()
      .then(res => setEntries(res.data))
      .catch(() => setError('Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const handleBook = async (entry) => {
    const key = entryKey(entry);
    setBooking(key);
    try {
      await premiumService.book(entry);
      setEntries(prev => prev.map(e =>
        entryKey(e) === key ? { ...e, alreadyBooked: true } : e
      ));
    } catch {
      // show inline error on the row
      setEntries(prev => prev.map(e =>
        entryKey(e) === key ? { ...e, bookingError: true } : e
      ));
    } finally {
      setBooking(null);
    }
  };

  const openDelete = (entry) => {
    setDeleteTarget(entry);
    setDeleteComment('');
    setDeleteError('');
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await premiumService.remove(deleteTarget.timesheetId, deleteComment);
      const name = deleteTarget.alias || deleteTarget.username;
      setEntries(prev => prev.filter(e => e.timesheetId !== deleteTarget.timesheetId));
      setNotice({
        text: `Zeiteintrag von ${name} vom ${formatDate(deleteTarget.originalBegin)} gelöscht. ${res.data.mailInfo}`,
        warn: !res.data.mailSent,
      });
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Der Zeiteintrag konnte nicht gelöscht werden.');
    } finally {
      setDeleting(false);
    }
  };

  const pending = entries.filter(e => !e.alreadyBooked);
  const booked  = entries.filter(e =>  e.alreadyBooked);

  return (
    <div className="dash-root">
      <Sidebar />

      <main className="dash-main">
        <header className="dash-header">
          <div>
            <h1 className="dash-title">Zeitzuschläge</h1>
            <p className="dash-date">Letzte {entries.length > 0 ? 'Einträge' : 'Tage'} aus dem Erfassungszeitraum</p>
          </div>
          {!loading && !error && (
            <div className="premium-summary">
              <span className="summary-chip pending">{pending.length} ausstehend</span>
              <span className="summary-chip booked">{booked.length} verbucht</span>
            </div>
          )}
        </header>

        {notice && (
          <div className={`premium-notice${notice.warn ? ' warn' : ''}`}>
            <span>{notice.text}</span>
            <button className="premium-notice-close" onClick={() => setNotice(null)} aria-label="Hinweis schließen">×</button>
          </div>
        )}

        {loading && (
          <div className="dash-state">
            <div className="dash-spinner" />
            <span>Lade Zeitzuschläge…</span>
          </div>
        )}
        {error && <div className="dash-state"><span className="dash-error-text">{error}</span></div>}

        {!loading && !error && entries.length === 0 && (
          <div className="dash-state"><span>Keine Zeitzuschläge im Erfassungszeitraum.</span></div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div className="table-card">
            <table className="premium-table">
              <thead>
                <tr>
                  <th>Mitarbeiter</th>
                  <th>Schicht</th>
                  <th>Art</th>
                  <th className="th-right">Bonuszeit</th>
                  <th className="th-center">Status</th>
                  <th className="th-center">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={entryKey(e)} className="premium-row" style={{ animationDelay: `${i * 45}ms` }}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{getInitials(e.alias, e.username)}</div>
                        <div className="user-meta">
                          <span className="user-name">{e.alias || e.username}</span>
                          {e.alias && <span className="user-sub">{e.username}</span>}
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="shift-cell">
                        <span className="shift-date">{formatDate(e.originalBegin)}</span>
                        <span className="shift-time">
                          {formatTime(e.originalBegin)} – {formatTime(e.originalEnd)}
                        </span>
                      </div>
                    </td>

                    <td>
                      <span className={`type-badge type-${e.type}`}>
                        {TYPE_LABELS[e.type] || e.type}
                      </span>
                    </td>

                    <td className="td-right">
                      <span className="bonus-value">{formatBonus(e.bonusDurationSeconds)}</span>
                    </td>

                    <td className="td-center">
                      {e.alreadyBooked
                        ? <span className="status-badge booked">Verbucht</span>
                        : <span className="status-badge pending">Ausstehend</span>
                      }
                    </td>

                    <td className="td-center">
                      {e.alreadyBooked ? (
                        <span className="action-done">✓</span>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="approve-btn"
                            disabled={booking === entryKey(e)}
                            onClick={() => handleBook(e)}
                          >
                            {booking === entryKey(e)
                              ? <span className="btn-spinner" />
                              : 'Genehmigen'}
                          </button>
                          <button
                            className="delete-btn"
                            disabled={booking === entryKey(e)}
                            onClick={() => openDelete(e)}
                          >
                            Löschen
                          </button>
                        </div>
                      )}
                      {e.bookingError && (
                        <span className="book-error">Fehler</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {deleteTarget && (
        <div className="pe-modal-overlay" onClick={closeDelete}>
          <div className="pe-modal" role="dialog" aria-modal="true" aria-labelledby="pe-modal-title"
               onClick={ev => ev.stopPropagation()}>
            <h2 id="pe-modal-title" className="pe-modal-title">Zeiteintrag löschen</h2>

            <div className="pe-modal-entry">
              <div className="pe-modal-row">
                <span>Mitarbeiter</span>
                <strong>{deleteTarget.alias || deleteTarget.username}</strong>
              </div>
              <div className="pe-modal-row">
                <span>Schicht</span>
                <strong>
                  {formatDate(deleteTarget.originalBegin)}, {formatTime(deleteTarget.originalBegin)} – {formatTime(deleteTarget.originalEnd)}
                </strong>
              </div>
              <div className="pe-modal-row">
                <span>Erkannter Zuschlag</span>
                <strong>{TYPE_LABELS[deleteTarget.type] || deleteTarget.type}</strong>
              </div>
            </div>

            <p className="pe-modal-warning">
              Der Zeiteintrag wird in Kimai endgültig gelöscht und fehlt danach im Zeitkonto.
              Das lässt sich nicht rückgängig machen. {deleteTarget.alias || deleteTarget.username} wird
              per E-Mail informiert.
            </p>

            <label className="pe-modal-label" htmlFor="pe-delete-comment">
              Kommentar an den Mitarbeitenden (optional)
            </label>
            <textarea
              id="pe-delete-comment"
              className="pe-modal-textarea"
              rows={4}
              maxLength={2000}
              placeholder="z. B. Grund für die Löschung"
              value={deleteComment}
              onChange={ev => setDeleteComment(ev.target.value)}
              disabled={deleting}
              autoFocus
            />

            {deleteError && <p className="pe-modal-error">{deleteError}</p>}

            <div className="pe-modal-actions">
              <button className="pe-cancel-btn" onClick={closeDelete} disabled={deleting}>
                Abbrechen
              </button>
              <button className="pe-confirm-delete-btn" onClick={handleDelete} disabled={deleting}>
                {deleting ? <span className="btn-spinner" /> : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function entryKey(e) {
  return `${e.userId}_${e.originalBegin}_${e.activityId}`;
}
